import React, { useState, useEffect } from 'react';
import { mediaAPI } from '../../services/api';
import { FaImages, FaPlus, FaTrash, FaEdit, FaVideo, FaMicrophone, FaTv, FaNewspaper, FaDownload } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const MediaSection = () => {
    const { language } = useLanguage();
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        type: 'photo',
        title: '',
        title_ar: '',
        media_url: '',
        duration: '',
        display_order: 0,
        thumbnail: null
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        loadMedia();
    }, []);

    const loadMedia = async () => {
        try {
            setLoading(true);
            const response = await mediaAPI.getAll('all');
            setMediaItems(response.data || []);
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => {
                if (key === 'thumbnail' && formData[key]) {
                    data.append('thumbnail', formData[key]);
                } else if (key !== 'thumbnail') {
                    data.append(key, formData[key]);
                }
            });

            if (editingId) {
                await mediaAPI.update(editingId, data);
            } else {
                await mediaAPI.create(data);
            }

            setFormData({
                type: 'photo',
                title: '',
                title_ar: '',
                media_url: '',
                duration: '',
                display_order: 0,
                thumbnail: null
            });
            setEditingId(null);
            loadMedia();
            alert('Enregistré avec succès!');
        } catch (error) {
            alert('Erreur: ' + error.message);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormData({
            type: item.type,
            title: item.title || '',
            title_ar: item.title_ar || '',
            media_url: item.media_url || '',
            duration: item.duration || '',
            display_order: item.display_order || 0,
            thumbnail: null
        });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer cet élément ?')) {
            try {
                await mediaAPI.delete(id);
                loadMedia();
            } catch (error) {
                alert('Erreur: ' + error.message);
            }
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'photo': return <FaImages />;
            case 'video': return <FaVideo />;
            case 'radio': return <FaMicrophone />;
            case 'tv': return <FaTv />;
            case 'press': return <FaNewspaper />;
            case 'download': return <FaDownload />;
            default: return <FaImages />;
        }
    };

    if (loading && mediaItems.length === 0) return <div style={styles.loading}>Chargement...</div>;

    return (
        <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <h1 style={styles.title}>🎬 {language === 'ar' ? 'إدارة الميديا' : 'Gestion du Media'}</h1>

            <div style={styles.grid}>
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>
                        <FaPlus style={{ marginRight: '10px', marginLeft: language === 'ar' ? '10px' : '0' }} />
                        {editingId ? (language === 'ar' ? 'تعديل' : 'Modifier') : (language === 'ar' ? 'إضافة جديد' : 'Ajouter')}
                    </h2>
                    <form onSubmit={handleSubmit} style={styles.form}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>{language === 'ar' ? 'النوع' : 'Type'}</label>
                            <select
                                value={formData.type}
                                onChange={e => setFormData({ ...formData, type: e.target.value })}
                                style={styles.input}
                            >
                                <option value="photo">{language === 'ar' ? 'صور' : 'Photo'}</option>
                                <option value="video">{language === 'ar' ? 'فيديو' : 'Vidéo'}</option>
                                <option value="tv">{language === 'ar' ? 'حوار تلفزي' : 'TV'}</option>
                                <option value="radio">{language === 'ar' ? 'حوار إذاعي' : 'Radio'}</option>
                                <option value="press">{language === 'ar' ? 'مقال صحفي' : 'Presse'}</option>
                                <option value="download">{language === 'ar' ? 'تحميل' : 'Téléchargement'}</option>
                            </select>
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Titre (FR)</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    style={styles.input}
                                    required
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>العنوان (AR)</label>
                                <input
                                    type="text"
                                    value={formData.title_ar}
                                    onChange={e => setFormData({ ...formData, title_ar: e.target.value })}
                                    style={{ ...styles.input, textAlign: 'right' }}
                                    required
                                />
                            </div>
                        </div>

                        {(formData.type !== 'photo' && formData.type !== 'download') && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>{language === 'ar' ? 'الرابط (Youtube / Facebook)' : 'Lien (Youtube / Facebook)'}</label>
                                <input
                                    type="text"
                                    value={formData.media_url}
                                    onChange={e => setFormData({ ...formData, media_url: e.target.value })}
                                    style={styles.input}
                                    placeholder="https://..."
                                />
                            </div>
                        )}

                        <div style={styles.formRow}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>{language === 'ar' ? 'الصورة (مصغرة)' : 'Image (Miniature)'}</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={e => setFormData({ ...formData, thumbnail: e.target.files[0] })}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>{language === 'ar' ? 'المدة (اختياري)' : 'Durée (ex: 05:20)'}</label>
                                <input
                                    type="text"
                                    value={formData.duration}
                                    onChange={e => setFormData({ ...formData, duration: e.target.value })}
                                    style={styles.input}
                                />
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{language === 'ar' ? 'الترتيب' : 'Ordre'}</label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={e => setFormData({ ...formData, display_order: e.target.value })}
                                style={styles.input}
                            />
                        </div>

                        <button type="submit" style={styles.submitBtn}>
                            {language === 'ar' ? 'حفظ' : 'Enregistrer'}
                        </button>
                        {editingId && (
                            <button type="button" onClick={() => { setEditingId(null); setFormData({ type: 'photo', title: '', title_ar: '', media_url: '', duration: '', display_order: 0, thumbnail: null }); }} style={{ ...styles.submitBtn, background: '#6c757d', marginTop: '5px' }}>
                                {language === 'ar' ? 'إلغاء' : 'Annuler'}
                            </button>
                        )}
                    </form>
                </div>

                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>{language === 'ar' ? 'قائمة الميديا' : 'Liste Media'} ({mediaItems.length})</h2>
                    <div style={styles.list}>
                        {mediaItems.length === 0 ? <p style={styles.empty}>Aucun élément</p> : (
                            mediaItems.map(item => (
                                <div key={item.id} style={styles.item}>
                                    <div style={styles.itemInfo}>
                                        <div style={styles.itemIcon}>{getTypeIcon(item.type)}</div>
                                        <div>
                                            <div style={styles.itemName}>{language === 'ar' ? item.title_ar : item.title}</div>
                                            <div style={styles.itemType}>{item.type} {item.duration && `· ${item.duration}`}</div>
                                        </div>
                                    </div>
                                    <div style={styles.actions}>
                                        <button onClick={() => handleEdit(item)} style={styles.editBtn}><FaEdit /></button>
                                        <button onClick={() => handleDelete(item)} style={styles.deleteBtn}><FaTrash /></button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    title: { fontSize: '28px', marginBottom: '20px', color: '#1a365d' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' },
    card: { background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    cardTitle: { fontSize: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center' },
    form: { display: 'flex', flexDirection: 'column', gap: '15px' },
    formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '13px', fontWeight: 'bold', color: '#666' },
    input: { padding: '10px', border: '1px solid #ddd', borderRadius: '8px' },
    submitBtn: { padding: '12px', background: '#1a365d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    list: { display: 'flex', flexDirection: 'column', gap: '10px' },
    item: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px' },
    itemInfo: { display: 'flex', alignItems: 'center', gap: '15px' },
    itemIcon: { fontSize: '20px', color: '#1a365d' },
    itemName: { fontWeight: 'bold' },
    itemType: { fontSize: '12px', color: '#888' },
    actions: { display: 'flex', gap: '5px' },
    editBtn: { background: '#ffc107', border: 'none', padding: '8px', borderRadius: '5px', cursor: 'pointer' },
    deleteBtn: { background: '#dc3545', color: 'white', border: 'none', padding: '8px', borderRadius: '5px', cursor: 'pointer' },
    empty: { textAlign: 'center', padding: '20px', color: '#888' },
    loading: { textAlign: 'center', padding: '40px' }
};

export default MediaSection;
